// Global idle detection for hidden section reveal
// Triggers hidden content discovery after 60+ seconds of inactivity on any page

import { navigateTo, router } from './router';
import { prefersReducedMotion } from './animations';

// Idle timeout in milliseconds (60 seconds)
const IDLE_TIMEOUT = 60000;

// Storage key for tracking idle discoveries
const IDLE_DISCOVERY_KEY = 'chopras-idle-discovery';

// Hidden sections that can be revealed through idle detection
const HIDDEN_SECTIONS = ['basement', 'attic', 'archive'] as const;
type HiddenSection = (typeof HIDDEN_SECTIONS)[number];

// State
let idleTimeout: number | null = null;
let isInitialized = false;
let idleHintElement: HTMLElement | null = null;

// Events to reset idle timer
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart', 'touchmove'];

/**
 * Get discovery state from localStorage
 */
function getIdleDiscoveryState(): Record<HiddenSection, boolean> {
  try {
    const stored = localStorage.getItem(IDLE_DISCOVERY_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore storage errors
  }
  return {
    basement: false,
    attic: false,
    archive: false,
  };
}

/**
 * Save idle discovery state to localStorage
 */
function saveIdleDiscoveryState(state: Record<HiddenSection, boolean>): void {
  try {
    localStorage.setItem(IDLE_DISCOVERY_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Mark a section as discovered via idle
 */
function markIdleDiscovery(section: HiddenSection): void {
  const state = getIdleDiscoveryState();
  state[section] = true;
  saveIdleDiscoveryState(state);
}

/**
 * Get the next undiscovered hidden section
 */
function getNextUndiscoveredSection(): HiddenSection | null {
  const state = getIdleDiscoveryState();

  // Return the first undiscovered section
  for (const section of HIDDEN_SECTIONS) {
    if (!state[section]) {
      return section;
    }
  }

  // All discovered - return a random one for revisits
  return HIDDEN_SECTIONS[Math.floor(Math.random() * HIDDEN_SECTIONS.length)];
}

/**
 * Show idle hint message
 */
function showIdleHint(): void {
  // Don't show hint on hidden pages themselves
  const currentPath = router.getCurrentPath();
  if (HIDDEN_SECTIONS.includes(currentPath as HiddenSection)) {
    resetIdleTimer();
    return;
  }

  // Check if we already have an active hint
  if (idleHintElement) {
    return;
  }

  const reducedMotion = prefersReducedMotion();
  const section = getNextUndiscoveredSection();

  // Create hint element
  const hint = document.createElement('div');
  hint.className = 'idle-hint';
  hint.setAttribute('role', 'alert');
  hint.setAttribute('aria-live', 'polite');

  // Different messages based on discovery state
  const state = getIdleDiscoveryState();
  const discoveredCount = Object.values(state).filter(Boolean).length;

  let message: string;
  let actionMessage: string;

  if (discoveredCount === 0) {
    message = '> the house stirs in the silence...';
    actionMessage = '> there are rooms not shown on any map';
  } else if (discoveredCount < 3) {
    message = '> you have been still too long...';
    actionMessage = `> ${3 - discoveredCount} hidden room${discoveredCount === 2 ? '' : 's'} remain${discoveredCount === 2 ? 's' : ''}`;
  } else {
    message = '> the manuscript remembers those who wait...';
    actionMessage = '> all rooms have revealed themselves';
  }

  hint.innerHTML = `
    <div class="idle-hint__content">
      <div class="idle-hint__message">${message}</div>
      <div class="idle-hint__action">${actionMessage}</div>
      ${section && discoveredCount < 3 ? `
        <button class="idle-hint__reveal" type="button">
          > ENTER THE ${section.toUpperCase()}
        </button>
      ` : ''}
    </div>
  `;

  // Apply styles
  hint.style.cssText = `
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.95), rgba(0, 0, 0, 0.8), transparent);
    padding: 2rem 1rem 1.5rem;
    z-index: 1000;
    font-family: 'IBM Plex Mono', monospace;
    opacity: ${reducedMotion ? '1' : '0'};
    transform: ${reducedMotion ? 'translateY(0)' : 'translateY(20px)'};
    ${reducedMotion ? '' : 'transition: opacity 1s ease-out, transform 1s ease-out;'}
  `;

  const content = hint.querySelector('.idle-hint__content') as HTMLElement;
  if (content) {
    content.style.cssText = `
      max-width: 600px;
      margin: 0 auto;
      text-align: center;
    `;
  }

  const messageEl = hint.querySelector('.idle-hint__message') as HTMLElement;
  if (messageEl) {
    messageEl.style.cssText = `
      color: var(--grey-light, #666);
      font-size: var(--font-sm, 0.875rem);
      margin-bottom: 0.5rem;
    `;
  }

  const actionEl = hint.querySelector('.idle-hint__action') as HTMLElement;
  if (actionEl) {
    actionEl.style.cssText = `
      color: var(--white-dim, #999);
      font-size: var(--font-sm, 0.875rem);
      margin-bottom: 1rem;
    `;
  }

  const revealBtn = hint.querySelector('.idle-hint__reveal') as HTMLButtonElement;
  if (revealBtn) {
    revealBtn.style.cssText = `
      background: transparent;
      border: 1px solid var(--red, #8b0000);
      color: var(--red, #8b0000);
      font-family: inherit;
      font-size: var(--font-sm, 0.875rem);
      padding: 0.75rem 1.5rem;
      cursor: pointer;
      transition: background 0.2s, color 0.2s;
    `;

    revealBtn.addEventListener('mouseenter', () => {
      revealBtn.style.background = 'var(--red, #8b0000)';
      revealBtn.style.color = 'var(--white, #f5f5f5)';
    });

    revealBtn.addEventListener('mouseleave', () => {
      revealBtn.style.background = 'transparent';
      revealBtn.style.color = 'var(--red, #8b0000)';
    });

    revealBtn.addEventListener('click', () => {
      if (section) {
        // Mark as discovered via idle
        markIdleDiscovery(section);

        // Show discovery feedback
        showDiscoveryFeedback(section);

        // Remove hint
        hideIdleHint();

        // Navigate after brief feedback
        setTimeout(() => {
          navigateTo(section);
        }, 500);
      }
    });
  }

  document.body.appendChild(hint);
  idleHintElement = hint;

  // Animate in
  if (!reducedMotion) {
    requestAnimationFrame(() => {
      hint.style.opacity = '1';
      hint.style.transform = 'translateY(0)';
    });
  }

  // Log to console for curious users
  console.log(
    '%c[IDLE] %cThe house notices your stillness...',
    'color: #8b0000; font-weight: bold',
    'color: #666'
  );

  // Auto-hide after 15 seconds if not interacted with
  setTimeout(() => {
    hideIdleHint();
  }, 15000);
}

/**
 * Show discovery feedback overlay
 */
function showDiscoveryFeedback(section: HiddenSection): void {
  const reducedMotion = prefersReducedMotion();

  const feedback = document.createElement('div');
  feedback.className = 'idle-discovery-feedback';
  feedback.innerHTML = `
    <div class="idle-discovery-feedback__inner">
      <span class="idle-discovery-feedback__text">> ACCESSING ${section.toUpperCase()}...</span>
    </div>
  `;

  feedback.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.95);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    ${reducedMotion ? 'opacity: 1;' : 'animation: idleFeedbackFlash 0.5s ease-out forwards;'}
  `;

  const inner = feedback.querySelector('.idle-discovery-feedback__inner') as HTMLElement;
  if (inner) {
    inner.style.cssText = `
      font-family: 'IBM Plex Mono', monospace;
      font-size: 1.25rem;
      color: var(--red, #8b0000);
      text-shadow: 0 0 10px rgba(139, 0, 0, 0.5);
    `;
  }

  // Add animation keyframes if needed
  if (!reducedMotion && !document.querySelector('#idle-feedback-styles')) {
    const style = document.createElement('style');
    style.id = 'idle-feedback-styles';
    style.textContent = `
      @keyframes idleFeedbackFlash {
        0% { opacity: 0; }
        20% { opacity: 1; }
        80% { opacity: 1; }
        100% { opacity: 0; }
      }
    `;
    document.head.appendChild(style);
  }

  document.body.appendChild(feedback);

  // Remove after animation
  setTimeout(() => {
    feedback.remove();
  }, 500);
}

/**
 * Hide idle hint
 */
function hideIdleHint(): void {
  if (!idleHintElement) return;

  const reducedMotion = prefersReducedMotion();

  if (reducedMotion) {
    idleHintElement.remove();
    idleHintElement = null;
  } else {
    idleHintElement.style.opacity = '0';
    idleHintElement.style.transform = 'translateY(20px)';

    const el = idleHintElement;
    setTimeout(() => {
      el.remove();
      if (idleHintElement === el) {
        idleHintElement = null;
      }
    }, 1000);
  }
}

/**
 * Reset the idle timer
 */
function resetIdleTimer(): void {
  // Hide any existing hint
  hideIdleHint();

  // Clear existing timeout
  if (idleTimeout !== null) {
    window.clearTimeout(idleTimeout);
  }

  // Set new timeout
  idleTimeout = window.setTimeout(() => {
    showIdleHint();
  }, IDLE_TIMEOUT);
}

/**
 * Initialize global idle detection
 * Should be called once when the app starts
 */
export function initIdleDetection(): void {
  if (isInitialized) return;
  isInitialized = true;

  // Setup activity listeners
  ACTIVITY_EVENTS.forEach(event => {
    document.addEventListener(event, resetIdleTimer, { passive: true });
  });

  // Start the timer
  resetIdleTimer();

  // Reset timer on route changes
  router.afterNavigate(() => {
    resetIdleTimer();
  });

  // Log initialization for curious console users
  console.log(
    '%c[IDLE] %cWatching for stillness... (60 seconds)',
    'color: #444',
    'color: #333'
  );
}

/**
 * Cleanup idle detection
 * Called when app is unmounted (rarely needed for SPAs)
 */
export function cleanupIdleDetection(): void {
  if (!isInitialized) return;

  // Clear timeout
  if (idleTimeout !== null) {
    window.clearTimeout(idleTimeout);
    idleTimeout = null;
  }

  // Remove activity listeners
  ACTIVITY_EVENTS.forEach(event => {
    document.removeEventListener(event, resetIdleTimer);
  });

  // Remove hint if present
  hideIdleHint();

  isInitialized = false;
}

/**
 * Get current idle discovery state (for debugging)
 */
export function getIdleDiscoveries(): Record<HiddenSection, boolean> {
  return getIdleDiscoveryState();
}

/**
 * Reset idle discoveries (for debugging)
 */
export function resetIdleDiscoveries(): void {
  try {
    localStorage.removeItem(IDLE_DISCOVERY_KEY);
  } catch {
    // Ignore
  }
}
